import previewSrc from '@fanconnect/neo-client/dist/show.html?url';
import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { DetailKey } from '~/components/info-pane';
import { StoppablePreview } from '~/components/preview';
import { ViewSelect } from '~/components/shows';
import { usePreviewControl } from '~/hooks/preview';
import { showPreviewProps } from '~/lib/preview';
import type { ShowPreview__Show } from '../../queries/preview.generated';

export interface ShowPreviewProps {
  show: ShowPreview__Show;
}

export const ShowPreview = ({ show }: ShowPreviewProps) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const viewId = Number(searchParams.get('viewId'));
  const setViewId = useCallback(
    (viewId: number | undefined) => {
      if (!viewId) return;
      setSearchParams(
        (prev) => {
          prev.set('viewId', String(viewId));
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (viewId) return;
    setSearchParams(
      (prev) => {
        prev.set('viewId', String(show.defaultView.id));
        return prev;
      },
      { replace: true },
    );
  }, [setSearchParams, show, viewId]);

  const previewProps = useMemo(() => showPreviewProps(show, viewId), [show, viewId]);

  const previewControl = usePreviewControl('ShowPreview');

  const views = show.views;

  return (
    <Box>
      {views.length > 1 && (
        <Box>
          <DetailKey as="div" id="show-view">
            <Typography variant="overline">{t('pages.shows.detail.preview.view_label')}</Typography>
          </DetailKey>
          <ViewSelect
            sx={{ mb: 2 }}
            show={show}
            showThumbnail
            fullWidth
            onChange={setViewId}
            value={viewId}
          />
        </Box>
      )}
      <StoppablePreview
        displayBackButton={Boolean(show.homeView)}
        previewControl={previewControl}
        src={previewSrc}
        {...previewProps}
      />
    </Box>
  );
};
